import React from 'react';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

interface DualAxisComboChartProps {
  data: {
    years: Array<{
      year: number;
      scope1: number;
      scope2: number;
      scope3: number;
      total: number;
    }>;
    baseYear: number;
  } | undefined;
}

export function DualAxisComboChart({ data }: DualAxisComboChartProps) {
  if (!data || !data.years || data.years.length === 0) return null;

  // Sort years to ensure proper order
  const sortedYears = [...data.years].sort((a, b) => a.year - b.year);
  
  const chartData = sortedYears.map(yearData => ({
    year: yearData.year,
    'Scope 1': yearData.scope1 / 1000, // Convert to metric tons
    'Scope 2': yearData.scope2 / 1000, // Convert to metric tons
    'Scope 3': yearData.scope3 / 1000, // Convert to metric tons
    'Total': yearData.total / 1000, // Convert to metric tons
    isBaseYear: yearData.year === data.baseYear,
  }));

  // Calculate percentage change
  const baseYearData = sortedYears.find(d => d.year === data.baseYear);
  const latestYearData = sortedYears[sortedYears.length - 1];
  const totalChange = baseYearData && latestYearData 
    ? ((latestYearData.total - baseYearData.total) / baseYearData.total) * 100 
    : 0;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const isBaseYear = label === data.baseYear;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-lg">{label}</p>
            {isBaseYear && (
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                Base Year
              </span>
            )}
          </div>
          <div className="space-y-2">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div 
                    className={`w-3 h-3 rounded-full ${
                      entry.dataKey === 'Total' ? 'bg-gradient-to-r from-purple-400 to-purple-600' : ''
                    }`}
                    style={{ 
                      backgroundColor: entry.dataKey === 'Total' ? undefined : entry.color 
                    }}
                  />
                  <span className="font-medium">{entry.dataKey}:</span>
                </div>
                <span className="font-bold">
                  {entry.value.toFixed(2)} t CO2e
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full min-w-0">
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 16,
            right: 16,
            left: 0,
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.6} />
          
          <XAxis 
            dataKey="year" 
            tick={{ fontSize: 12, fill: '#374151', fontWeight: '500' }}
            axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
            tickLine={{ stroke: '#d1d5db' }}
          />
          
          <YAxis 
            yAxisId="left"
            width={48}
            tick={{ fontSize: 11, fill: '#374151' }}
            axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
            tickLine={{ stroke: '#d1d5db' }}
            tickFormatter={(value) => `${value.toFixed(1)}t`}
          />
          
          <YAxis 
            yAxisId="right"
            orientation="right"
            width={48}
            tick={{ fontSize: 11, fill: '#374151' }}
            axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
            tickLine={{ stroke: '#d1d5db' }}
            tickFormatter={(value) => `${value.toFixed(1)}t`}
          />
          
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          
          {/* Base Year Reference Line */}
          <ReferenceLine 
            yAxisId="left"
            x={data.baseYear} 
            stroke="#f97316" 
            strokeWidth={3}
            strokeDasharray="8 4"
            label={{ 
              value: "Base Year", 
              position: "top",
              style: { 
                fill: '#f97316', 
                fontSize: '14px', 
                fontWeight: 'bold' 
              }
            }}
          />

          {/* Scope Bars */}
          <Bar 
            yAxisId="left"
            dataKey="Scope 1" 
            stackId="a"
            fill="#dc2626" 
            name="Scope 1"
          />
          <Bar 
            yAxisId="left"
            dataKey="Scope 2" 
            stackId="a"
            fill="#16a34a" 
            name="Scope 2"
          />
          <Bar 
            yAxisId="left"
            dataKey="Scope 3" 
            stackId="a"
            fill="#2563eb" 
            name="Scope 3"
          />
          
          {/* Total Line */}
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="Total" 
            stroke="#7c3aed" 
            strokeWidth={4}
            dot={{
              fill: '#7c3aed',
              strokeWidth: 3,
              r: 6,
              stroke: '#ffffff'
            }}
            activeDot={{
              r: 8,
              stroke: '#7c3aed',
              strokeWidth: 3,
              fill: '#ffffff'
            }}
            name="Total Emissions"
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      {baseYearData && latestYearData && (
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Base Year ({data.baseYear})</div>
            <div className="text-lg font-bold text-gray-900">
              {(baseYearData.total / 1000).toFixed(2)}t
            </div>
            <div className="text-xs text-gray-500">Total CO2e</div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Latest Year ({latestYearData.year})</div>
            <div className="text-lg font-bold text-gray-900">
              {(latestYearData.total / 1000).toFixed(2)}t
            </div>
            <div className="text-xs text-gray-500">Total CO2e</div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Absolute Change</div>
            <div className={`text-lg font-bold ${
              (latestYearData.total - baseYearData.total) > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {((latestYearData.total - baseYearData.total) > 0 ? '+' : '')}
              {((latestYearData.total - baseYearData.total) / 1000).toFixed(2)}t
            </div>
            <div className="text-xs text-gray-500">CO2e difference</div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Percentage Change</div>
            <div className={`text-lg font-bold ${
              totalChange > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {totalChange > 0 ? '+' : ''}{totalChange.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500">vs base year</div>
          </div>
        </div>
      )}
    </div>
  );
}
